import React, { useCallback, useEffect, useRef, useState } from "react";
import { UpdateInfo, UpdateOptions } from "../CreateUpdatePage";
import { Button, ButtonToolbar, Table } from "react-bootstrap";
import { releaseAPI } from "../../api/releaseAPI";
import ErrorDisplay from "../../common/ErrorDisplay";
import { Problem, Result } from "../../api/backendApi";
import {
  drawingApi,
  DrawingTypeModel,
  LocaleModel,
} from "../../api/drawingApi";
import { AttributeWindowTextModel, glossaryApi } from "../../api/glossaryApi";

interface UpdateFileProcessingProps {
  updateOptions: UpdateOptions;
  updateInfoList: UpdateInfo[] | undefined;
  onFinish: (success: boolean) => void;
}

enum ProcessingStatus {
  NotProcessed = 1,
  Queued = 2,
  Processing = 3,
  Ok = 4,
  Errors = 5,
  Failed = 6,
  MissingTranslations = 7,
}

interface VehicleFileState {
  vehicleFileNumber: string | undefined;
  status: ProcessingStatus;
  messages: string[];
}

function UpdateFileProcessing({
  updateOptions,
  updateInfoList,
  onFinish,
}: UpdateFileProcessingProps) {
  const [files, setFiles] = useState<VehicleFileState[]>();
  const [executing, setExecuting] = useState(false);
  const [queue, setQueue] = useState<(string | undefined)[]>([]);
  const [error, setError] = useState<Problem>();

  const scrollRowIntoView = (index: number) => {
    const rows = tableRef?.current?.rows;

    if (!rows) return;

    const row = rows.item(index < rows.length ? index : rows.length - 1);

    if (row) {
      row.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
    }
  };

  useEffect(() => {
    const filtered = updateInfoList?.filter(
      (ui) => ui.include && ui.hasCrsFile,
    );
    const mapped =
      filtered?.map((ui) => ({
        vehicleFileNumber: ui.vehicleFileNumber,
        status: ProcessingStatus.NotProcessed,
        messages: [],
      })) ?? [];

    const globalValidationStep: VehicleFileState = {
      vehicleFileNumber: undefined,
      status: ProcessingStatus.NotProcessed,
      messages: [],
    };

    setFiles([globalValidationStep, ...mapped]);
  }, [updateInfoList]);

  const stateUpdateHelper = (
    vehicleFileNumber: string | undefined,
    status: ProcessingStatus,
    messages: string[],
  ) => {
    return files?.map((f) =>
      f.vehicleFileNumber !== vehicleFileNumber
        ? f
        : { ...f, status, messages },
    );
  };

  useEffect(() => {
    async function processEntry(vehicleFileNumber: string | undefined) {
      const rowIndex = files?.findIndex(
        (f) => f.vehicleFileNumber === vehicleFileNumber,
      );

      // the +1 is because it always leaves one at the end for some reason
      if (rowIndex) scrollRowIntoView(rowIndex + 1);

      setFiles(
        stateUpdateHelper(vehicleFileNumber, ProcessingStatus.Processing, []),
      );

      let messages: string[] = [];
      let status: ProcessingStatus;

      if (vehicleFileNumber) {
        // Check vehicle file for missing translations
        const missingTranslations =
          await getMissingTranslations(vehicleFileNumber);

        if (missingTranslations && missingTranslations.length > 0) {
          setFiles(
            stateUpdateHelper(
              vehicleFileNumber,
              ProcessingStatus.MissingTranslations,
              missingTranslations,
            ),
          );
          setQueue(queue.filter((f) => f !== vehicleFileNumber));
          setExecuting(false);
          return;
        }

        const response = await releaseAPI.processVehicleFile(
          vehicleFileNumber,
          updateOptions.skipExistingMpfzFiles,
        );

        if (response.ok) {
          setError(undefined);
          messages = response.value.messages;
          status = response.value.success
            ? ProcessingStatus.Ok
            : ProcessingStatus.Errors;
        } else {
          setError(response.problem);
          status = ProcessingStatus.Failed;
        }
      } else {
        const response = await releaseAPI.performPreReleaseValidations();

        if (response.ok) {
          setError(undefined);
          messages = response.value;
          status = ProcessingStatus.Ok;
        } else {
          setError(response.problem);
          status = ProcessingStatus.Failed;
        }
      }

      if (!vehicleFileNumber && status !== ProcessingStatus.Ok) {
        // if it's the global validation step do not carry on.
        setFiles(
          files?.map((f) =>
            f.vehicleFileNumber === vehicleFileNumber
              ? { ...f, status: status, messages: messages }
              : { ...f, status: ProcessingStatus.NotProcessed },
          ),
        );

        setQueue([]);
      } else {
        setFiles(stateUpdateHelper(vehicleFileNumber, status, messages));

        setQueue(queue.filter((f) => f !== vehicleFileNumber));
      }
    }

    if (executing) {
      if (queue.length === 0) {
        setExecuting(false);

        const success: boolean =
          files !== undefined &&
          files !== null &&
          files.length > 0 &&
          files.filter((f) => f.status === ProcessingStatus.Ok).length ===
            files.length;

        onFinish(success);

        if (files) scrollRowIntoView(files.length);
      } else {
        processEntry(queue[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue]);

  const getMissingTranslations = useCallback(
    async (vehicleFileNumber: string): Promise<string[]> => {
      const checkAttributeWindow = async (
        name: string,
        isInformationWindow: boolean,
      ): Promise<Result<string[]>> => {
        const awResponse = await drawingApi.getAttributeWindow(
          vehicleFileNumber,
          DrawingTypeModel.Drawing,
          name,
          isInformationWindow,
        );

        if (!awResponse.ok) {
          return awResponse;
        }

        const emptyTitleItems = awResponse.value.filter(
          (attributeWindow) => !attributeWindow.title || attributeWindow.title.trim() === "",
        );

        if (emptyTitleItems.length > 0) {
          return {
            ok: true,
            value: emptyTitleItems.map(() =>
                `${isInformationWindow ? "Information" : "Attribute"} window "${name}" has an empty title`,
            ),
          };
        }

        const translatedResponse =
          await glossaryApi.getTranslatedAttributeWindows(
            awResponse.value.map(
              (x) => new AttributeWindowTextModel(x.title ?? "", x.text ?? ""),
            ),
            [LocaleModel.English],
          );

        if (!translatedResponse.ok) {
          return translatedResponse;
        }

        const templatedResponse =
          await glossaryApi.getTemplatedAttributeWindows(
            vehicleFileNumber,
            translatedResponse.value,
          );

        if (!templatedResponse.ok) {
          return templatedResponse;
        }

        if (templatedResponse.value.unmatchedStrings.length > 0) {
          return {
            ok: true,
            value: templatedResponse.value.unmatchedStrings.map(
              (s) => `${isInformationWindow ? "Information" : "Attribute"} window "${name}" contains text not found in glossary: "${s}"`,
            ),
          };
        }

        const missingTranslationsResponse =
          await glossaryApi.getMissingTranslations(
            templatedResponse.value.templates,
          );

        if (!missingTranslationsResponse.ok) {
          return missingTranslationsResponse;
        }

        return {
          ok: true,
          value: missingTranslationsResponse.value.missingTranslations,
        };
      };

      const treeResponse = await drawingApi.getDrawingTree(
        vehicleFileNumber,
        DrawingTypeModel.Drawing,
      );

      if (!treeResponse.ok) {
        return [treeResponse.problem.title ?? "Unknown error"];
      }

      const missingTranslationsList: string[] = [];

      for (let ii = 0; ii < treeResponse.value.attributeWindows.length; ++ii) {
        const check = await checkAttributeWindow(
          treeResponse.value.attributeWindows[ii].name,
          false,
        );

        if (!check.ok) {
          return [check.problem.title ?? "Unknown error"];
        }

        if (check.value && check.value.length > 0) {
          missingTranslationsList.push(...check.value);
        }
      }

      for (
        let ii = 0;
        ii < treeResponse.value.informationWindows.length;
        ++ii
      ) {
        const check = await checkAttributeWindow(
          treeResponse.value.informationWindows[ii].name,
          true,
        );

        if (!check.ok) {
          return [check.problem.title ?? "Unknown error"];
        }

        if (check.value && check.value.length > 0) {
          missingTranslationsList.push(...check.value);
        }
      }

      setError(undefined);

      // Return true if all attribute/information windows are complete
      return missingTranslationsList;
    },
    [],
  );

  const handleExecute = () => {
    if (!files) return;

    setExecuting(true);
    setFiles(
      files.map((f) => ({
        ...f,
        status: ProcessingStatus.Queued,
        messages: [],
      })),
    );
    setQueue(files.map((f) => f.vehicleFileNumber));
  };

  const handleCancel = () => {
    setExecuting(false);
    setQueue([]);
  };

  const tableRef = useRef<HTMLTableElement>(null);

  return (
    <div className="h-100" style={{ display: "flex", flexDirection: "column" }}>
      <ErrorDisplay error={error} />
      <div className="fixed-table-header" style={{ height: "100%" }}>
        <Table ref={tableRef} size="sm" style={{ fontSize: "80%" }}>
          <thead>
            <tr>
              <th>Processing step</th>
              <th>Status</th>
              <th>Messages</th>
            </tr>
          </thead>
          <tbody>
            {!files && (
              <tr>
                <td colSpan={3}>loading...</td>
              </tr>
            )}
            {files && files.length === 0 && (
              <tr>
                <td colSpan={3}>
                  No vehicle files are to be included in the update...
                </td>
              </tr>
            )}
            {files &&
              files.map((ui) => (
                <tr key={ui.vehicleFileNumber ?? ""}>
                  <td className="shrink-nowrap">
                    {ui.vehicleFileNumber ?? "Global validations"}
                  </td>
                  <td style={{ width: "8em" }}>
                    {ProcessingStatus[ui.status]}
                  </td>
                  <td
                    title={
                      ui.messages.length > 1 ? ui.messages.join("\r\n") : ""
                    }
                    style={{ cursor: ui.messages.length > 1 ? "help" : "" }}
                  >
                    {ui.messages.length <= 1
                      ? (ui.messages[0] ?? "")
                      : "Errors found. Hover with the mouse to see them."}
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>
      </div>
      <ButtonToolbar className="my-2">
        <Button
          size="sm"
          variant="success"
          className="me-2"
          disabled={executing}
          onClick={() => handleExecute()}
        >
          Execute
        </Button>
        <Button
          size="sm"
          variant="outline-warning"
          className="me-2"
          disabled={!executing}
          onClick={() => handleCancel()}
        >
          Cancel
        </Button>
      </ButtonToolbar>
    </div>
  );
}

export default UpdateFileProcessing;
